import { NavLink, Outlet } from 'react-router-dom'

/*
  One path for everything configurable.

  WHY THIS EXISTS

  It did not, and the editors ended up wherever they were first needed: bed types on the
  Occupancy screen behind a mode toggle, schedules and alert recipients at the bottom of
  the Run Console, the organization's time zone two levels down inside the schedule panel,
  branding in the user menu. Every one of them was reachable and none of them was
  findable — the bed-type editor was reported as missing when it had been built and
  shipped.

  Scattering configuration across the screens that consume it is a reasonable-looking
  local decision made five separate times. The cost only appears in aggregate: there is no
  answer to "where do I change things?", so the answer becomes "ask someone".

  NOTHING IS DUPLICATED. These are the same components, rendered here instead of there.
  A setting reachable from two places is a setting that will be described two ways and
  eventually behave two ways.

  Each screen keeps its own permission check rather than being hidden from the list --
  the same reasoning the main navigation applies to Audit. An item that vanishes reads as
  "this product cannot do that", which is a worse thing to believe than "you cannot".
*/
const sections = [
  {
    to: 'overview',
    label: 'Configuration overview',
    hint: 'How accounts, drivers and cost centers connect — and where they do not',
  },
  {
    to: 'occupancy',
    label: 'Occupancy & bed types',
    hint: 'Patient-day mode, and the bed types occupancy is recorded against',
  },
  {
    to: 'schedules',
    label: 'Schedules',
    hint: 'Unattended runs, and the time zone their crons are read in',
  },
  {
    to: 'alerts',
    label: 'Alerts',
    hint: 'Who to tell when a scheduled run fails',
  },
  {
    to: 'branding',
    label: 'Branding',
    hint: 'Name, marks, palette and type',
  },
]

export function Settings() {
  return (
    <div className="flex h-full min-h-0">
      <nav
        className="w-60 shrink-0 overflow-auto border-r py-2"
        style={{ background: 'var(--color-panel-alt)', borderColor: 'var(--color-rule)' }}
        aria-label="Settings"
      >
        <h1 className="col-head px-4 py-1.5">settings</h1>

        {sections.map((section) => (
          <NavLink
            key={section.to}
            to={section.to}
            className="block border-l-2 px-4 py-2"
            style={({ isActive }) => ({
              borderColor: isActive ? 'var(--color-accent)' : 'transparent',
              background: isActive ? 'var(--color-panel)' : 'transparent',
            })}
          >
            {({ isActive }) => (
              <>
                <span
                  className="block text-[13px]"
                  style={{
                    color: isActive ? 'var(--color-accent)' : 'var(--color-ink)',
                    fontWeight: isActive ? 500 : 400,
                  }}
                >
                  {section.label}
                </span>
                {/*
                    A one-line hint per item, not a tooltip. Half of these are things
                    somebody touches once per installation, and the name alone does not
                    say whether it is the one they want.
                */}
                <span
                  className="mt-0.5 block text-[11px] leading-snug"
                  style={{ color: 'var(--color-faint)' }}
                >
                  {section.hint}
                </span>
              </>
            )}
          </NavLink>
        ))}
      </nav>

      <div className="min-h-0 flex-1 overflow-auto">
        <Outlet />
      </div>
    </div>
  )
}
